import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';

import {
  SnackbarHostComponent,
  type SnackbarHostState,
  type SnackbarVariant,
} from './snackbar-host.component';

export interface SnackbarOptions {
  variant?: SnackbarVariant;
  /** Auto-dismiss duration in ms. 0 = sticky until dismissed manually. */
  duration?: number;
  /** Label of the close button. Defaults to the i18n "Close". */
  closeLabel?: string;
}

/**
 * Toast notification host. Replaces MatSnackBar with a single fixed-position
 * host that stacks N concurrent toasts at the bottom-right and animates each
 * in/out independently.
 *
 * One host is lazily created on the first show() call and re-used for every
 * subsequent toast — no DOM churn between rapid successive notifications.
 */
@Injectable({ providedIn: 'root' })
export class SnackbarService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);

  private hostRef: ComponentRef<SnackbarHostComponent> | null = null;
  private nextId = 1;

  show(message: string, options: SnackbarOptions = {}): void {
    const host = this.ensureHost();
    const variant: SnackbarVariant = options.variant ?? 'info';
    const duration = options.duration ?? (variant === 'error' ? 5000 : 3000);
    const id = this.nextId++;
    const entry: SnackbarHostState = {
      id,
      message,
      variant,
      closeLabel: options.closeLabel ?? $localize`:@@snackbar.close:Close`,
    };
    host.instance.push(entry);
    if (duration > 0) {
      setTimeout(() => host.instance.dismiss(id), duration);
    }
  }

  private ensureHost(): ComponentRef<SnackbarHostComponent> {
    if (this.hostRef) {
      return this.hostRef;
    }
    // Letting createComponent build its own host node keeps the
    // <app-snackbar-host> custom-element tag in the DOM; using hostElement
    // would swap it for an anonymous <div> and break any selector that
    // expects the component tag.
    this.hostRef = createComponent(SnackbarHostComponent, {
      environmentInjector: this.envInjector,
    });
    this.appRef.attachView(this.hostRef.hostView);
    document.body.appendChild(this.hostRef.location.nativeElement as Node);
    return this.hostRef;
  }
}
